import type { CatalogKey } from "@/actions/catalog";
import type { BarContext } from "./conditions";
import { FAMILIES, type FamilyId } from "./families";

/** Commit mode's section: always first, and never folded or counted as an icon. */
export const COMMIT_SCOPE: ReadonlySet<FamilyId> = new Set([
  "discard",
  "commit",
]);
import {
  ACTIONS,
  applicable,
  entryOf,
  type Accent,
  type ActionId,
} from "./registry";
import type { Selection, SelectionState } from "./selection";

export type SlotAccent = Accent | null;

export interface Slot {
  /** Set on a ⋮ action drawn as its own icon; a family's slot is its family. */
  key?: string;
  family: FamilyId;
  members: ActionId[];
  /** `null` for `⋮`, a menu; any other slot is one action. */
  defaultAction: ActionId | null;
  /** What each member wears for this selection. */
  entries: Partial<Record<ActionId, CatalogKey>>;
  /** The members that colour their slot or row. */
  accents: Partial<Record<ActionId, Accent>>;
}

/** An empty table's own bar; its column keeps the column bar and Set data type. */
const EMPTY_TABLE_FALLBACK: SelectionState = "none";

const BY_SORT = (Object.keys(FAMILIES) as FamilyId[]).sort(
  (a, b) => FAMILIES[a].sort - FAMILIES[b].sort,
);

/** Left to right, as the bar draws their families. */
const barOrder = (a: ActionId, b: ActionId) =>
  FAMILIES[ACTIONS[a].family].sort - FAMILIES[ACTIONS[b].family].sort ||
  ACTIONS[a].order - ACTIONS[b].order;

/** An icon runs exactly what its tooltip names: one slot per family holds its
 *  default (and that default's ⌥ variant), and every other member that applies
 *  is a row of ⋮. Unfolded: the bar folds. */
export function arrange(selection: Selection, x: BarContext): Slot[] {
  const state =
    x.tableIsEmpty && selection.state === "single-tview"
      ? EMPTY_TABLE_FALLBACK
      : selection.state;
  const ids = applicable(state, x);
  const icons = new Set(
    BY_SORT.filter((family) => family !== "more").flatMap((family) => {
      const icon = ids.find(
        (id) =>
          ACTIONS[id].family === family &&
          !ACTIONS[id].reverses &&
          !ACTIONS[id].menuOnly,
      );
      const alt = icon && ACTIONS[icon].alt;
      return icon ? [icon, ...(alt && ids.includes(alt) ? [alt] : [])] : [];
    }),
  );
  const familyOf = (id: ActionId): FamilyId =>
    icons.has(id) ? ACTIONS[id].family : "more";

  return BY_SORT.map((family) => ({
    family,
    members: ids.filter((id) => familyOf(id) === family).sort(barOrder),
  }))
    .filter(({ members }) => members.length > 0)
    .map(({ family, members }) => {
      const defaultAction =
        family === "more"
          ? null
          : (members.find((id) => !ACTIONS[id].reverses) ?? null);
      const entries = Object.fromEntries(
        members.map((id) => [id, entryOf(id, x)]),
      );
      const accents = Object.fromEntries(
        members.flatMap((id) => {
          const accent = ACTIONS[id].accent?.(x);
          return accent ? [[id, accent]] : [];
        }),
      );
      return {
        family,
        members,
        defaultAction,
        entries,
        accents,
      };
    });
}

export const entryKeyOf = (slot: Slot, id: ActionId): CatalogKey =>
  slot.entries[id]!;

export const slotAccent = (slot: Slot): SlotAccent =>
  (slot.defaultAction && slot.accents[slot.defaultAction]) || null;

const alone = (more: Slot, id: ActionId): Slot => ({
  key: `more:${id}`,
  family: "more",
  members: [id],
  defaultAction: id,
  entries: { [id]: entryKeyOf(more, id) },
  accents: {},
});

export const slotKey = (slot: Slot): string => slot.key ?? slot.family;

/** Icons the bar shows before the rest fold into ⋮. */
export const MAX_ICONS = 5;
/** A ⋮ that would hold fewer actions shows them as icons instead. */
const MIN_MENU = 2;

/** One icon per family, then ⋮'s own actions as icons of their own. Past
 *  `maxIcons` the rest fold into ⋮, unless it would then hold fewer than
 *  `MIN_MENU` actions and the bar has `room` to show them all. */
export function fold(
  slots: Slot[],
  maxIcons = MAX_ICONS,
  room = Infinity,
): Slot[] {
  const scope = slots.filter((s) => COMMIT_SCOPE.has(s.family));
  const rest = slots.filter((s) => !COMMIT_SCOPE.has(s.family));
  const left = room - scope.length;
  return [
    ...scope,
    ...foldActions(rest, Math.max(0, Math.min(maxIcons, left - 1)), left),
  ];
}

function foldActions(slots: Slot[], maxIcons: number, room: number): Slot[] {
  const more = slots.find((s) => s.family === "more");
  // A member moved here from its family is never an icon, so ⋮ stays a menu.
  const kept = more?.members.some((id) => ACTIONS[id].family !== "more")
    ? more
    : undefined;
  const items = [
    ...slots.filter((s) => s !== more),
    ...(kept ? [] : (more?.members ?? []).map((id) => alone(more!, id))),
  ];
  const folded = [...items.slice(maxIcons), ...(kept ? [kept] : [])];
  const members = folded.flatMap((s) => s.members).sort(barOrder);
  if (!kept && members.length < MIN_MENU && items.length <= room) return items;
  return [
    ...items.slice(0, maxIcons),
    {
      family: "more",
      members,
      defaultAction: null,
      entries: Object.assign({}, ...folded.map((s) => s.entries)),
      accents: Object.assign({}, ...folded.map((s) => s.accents)),
    },
  ];
}
